import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import type { OnboardingStatus, OnboardingSteps } from '@/types/api';

export const ONBOARDING_STATUS_KEY = ['onboarding', 'status'] as const;

/** The wizard's steps, in order. The key is also the URL segment: /onboarding/<key>. */
export const ONBOARDING_STEPS: { key: keyof OnboardingSteps; title: string; checklistLabel: string }[] = [
  { key: 'business', title: 'Your business', checklistLabel: 'Add your business details' },
  { key: 'channel', title: 'Connect a channel', checklistLabel: 'Connect Facebook, Instagram or WhatsApp' },
  { key: 'knowledge', title: 'Teach the AI', checklistLabel: 'Teach the AI about your business' },
  { key: 'plan', title: 'Choose a plan', checklistLabel: 'Choose a plan' },
];

export function useOnboardingStatus() {
  return useQuery({
    queryKey: ONBOARDING_STATUS_KEY,
    queryFn: () => api.get<OnboardingStatus>('/api/onboarding/status'),
    // Meta's OAuth runs in a separate tab. Refetching when this tab regains focus ticks the
    // channel step as soon as the user comes back.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ completedAt: string }>('/api/onboarding/complete'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ONBOARDING_STATUS_KEY }),
  });
}
